import { DisplayColumnItem, PanelHeader, PopoverContainer, ScrollableContent, StyledSwitch } from "@/components/commons/filter-display-settings-styled";
import {
  MATCHING_RESULTS_COLUMNS,
  TMatchingResultsColumnId,
  resolveColumnLabel,
} from "@/features/dashboard/constants/matching-results-columns";
import { TMatchingResultsMetric } from "@/features/dashboard/types/matching-results";
import { Popover, Stack, Typography } from "@mui/material";
import { FC } from "react";

// Columns that users cannot hide (empty table prevention).
export const LOCKED_COLUMN_IDS: readonly TMatchingResultsColumnId[] = ["status", "talentName"];

type TDisplayColumnsPopoverProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  visibleIds: Set<TMatchingResultsColumnId>;
  onToggle: (id: TMatchingResultsColumnId) => void;
  metric: TMatchingResultsMetric;
};

/**
 * Popover of column-visibility switches. Write-through via parent's `onToggle`.
 * Locked columns rendered with disabled switch.
 */
export const DisplayColumnsPopover: FC<TDisplayColumnsPopoverProps> = ({ anchorEl, onClose, visibleIds, onToggle, metric }) => {
  const isOpen = Boolean(anchorEl);

  return (
    <Popover
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{ paper: { sx: { mt: 1 } } }}
    >
      <PopoverContainer>
        <PanelHeader>
          <Typography variant="sub16Semi">表示項目設定</Typography>
        </PanelHeader>
        <ScrollableContent>
          {MATCHING_RESULTS_COLUMNS.map((col) => {
            const isLocked = LOCKED_COLUMN_IDS.includes(col.id);
            const isVisible = visibleIds.has(col.id);
            const label = resolveColumnLabel(col, metric);

            return (
              <DisplayColumnItem key={col.id} isDragging={false}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" px={1}>
                  <Typography variant="body14">
                    {label}
                    {isLocked && (
                      <Typography component="span" variant="cap12" sx={{ ml: 1, color: "text.secondary" }}>
                        （必須）
                      </Typography>
                    )}
                  </Typography>
                  <StyledSwitch
                    checked={isVisible}
                    disabled={isLocked}
                    onChange={() => onToggle(col.id)}
                  />
                </Stack>
              </DisplayColumnItem>
            );
          })}
        </ScrollableContent>
      </PopoverContainer>
    </Popover>
  );
};
